import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  endorsementCatalog,
  endorsements,
  type EndorsementDetail,
} from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Card, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Pagination } from '@/shared/ui/Pagination';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';

import { caminhoDoAssistente } from './endosso';

/**
 * Endossos de cancelamento na fila de cancelamentos (P-END-2; decisao 3 da 11.7 do
 * plano 49). O cancelamento e um tipo de endosso, com a data, a conta da restituicao e
 * a cotacao de endosso na esteira; a emissao espera a regra de restituicao. Ate la, a
 * solicitacao de cancelamento sem restituicao continua acima, e os dois nao convivem na
 * mesma apolice.
 */

const limite = 10;

export function CancellationEndorsementsSection() {
  const [offset, setOffset] = useState(0);
  const tipos = useQuery({
    queryKey: ['endorsement-types', 'todos'],
    queryFn: ({ signal }) => endorsementCatalog.types(false, signal),
    retry: false,
  });
  const tipo = tipos.data?.find((item) => item.code === 'cancelamento') ?? null;
  const consulta = useQuery({
    queryKey: ['endorsements', { tipo: tipo?.id ?? '', offset, cancelamentos: true }],
    queryFn: ({ signal }) =>
      endorsements.list(
        { endorsement_type_id: tipo?.id ?? '', limit: limite, offset },
        signal,
      ),
    enabled: tipo !== null,
  });

  const colunas: Column<EndorsementDetail>[] = [
    {
      key: 'numero',
      header: 'Endosso',
      render: (endosso) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{endosso.number}</p>
          <p className="truncate text-2xs text-ink-500">
            {endosso.endorsement_reason?.name ?? endosso.reason}
          </p>
        </div>
      ),
    },
    {
      key: 'apolice',
      header: 'Apolice',
      render: (endosso) =>
        endosso.policy ? (
          <div className="min-w-0">
            <p className="text-ink-900">
              {endosso.policy.external_number || String(endosso.policy.number)}
            </p>
            <p className="truncate text-2xs text-ink-500">
              {endosso.policy.policyholder_name}
            </p>
          </div>
        ) : (
          '--'
        ),
    },
    {
      key: 'operacao',
      header: 'Cotacao de endosso',
      hideBelow: 'md',
      render: (endosso) =>
        endosso.operation ? (
          <Link
            to={`/operacoes/${endosso.operation.id}`}
            className="text-brand-600 underline-offset-2 hover:underline"
          >
            {endosso.operation.number} -{' '}
            {statusLabel('operacao', endosso.operation.stage)}
          </Link>
        ) : (
          <span className="text-ink-500">--</span>
        ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (endosso) => (
        <StatusBadge kind="situacaoEndosso" value={endosso.status} />
      ),
    },
    {
      key: 'acao',
      header: 'Endosso',
      align: 'right',
      render: (endosso) =>
        endosso.endorsement_subtype_id ? (
          <Link
            to={caminhoDoAssistente(
              endosso.policy_id,
              'cancelamento',
              endosso.endorsement_subtype_id,
              endosso.id,
            )}
            className="text-sm text-brand-600 underline-offset-2 hover:underline"
          >
            Abrir o endosso
          </Link>
        ) : null,
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Endossos de cancelamento"
        description="O cancelamento pelo assistente de endosso, com a data, a conta para a restituicao e a cotacao de endosso na esteira. A emissao espera a regra de restituicao (END-005, PRC-010)."
      />
      {tipos.isError || consulta.isError ? (
        <div className="px-4 pb-3">
          <Alert
            tone="danger"
            title="Nao foi possivel carregar os endossos de cancelamento"
          >
            {(tipos.error ?? consulta.error)?.message}
          </Alert>
        </div>
      ) : null}
      <DataTable
        columns={colunas}
        items={consulta.data?.items ?? []}
        keyOf={(endosso) => endosso.id}
        loading={consulta.isPending && tipo !== null}
        empty={
          <EmptyState
            icon="close"
            title="Nenhum endosso de cancelamento"
            description="O endosso de cancelamento nasce na apolice: Novo endosso, Endosso de cancelamento."
          />
        }
      />
      <Pagination
        total={consulta.data?.total ?? 0}
        limit={limite}
        offset={offset}
        onChange={setOffset}
      />
    </Card>
  );
}
